import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Button from '../../../../../Button';
import CardDesignerCardElementColorControl from '../../../CardDesignerCardElements/CardDesignerCardElementControls/components/CardDesignerCardElementColorControl';
import Label from '../../../../../Label';
import Input from '../../../../../Input';
import Alert from '../../../../../Alert';
import ImageUpload from '../../../../../ImageUpload';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import useDismissiblePersistent from '../../../../../../../hooks/useDismissiblePersistent';
import useAccountPrintAssets from '../../../../../../../apps/pro/views/Account/AccountPrintAssets/components/AccountPrintAsset/hooks/useAccountPrintAssets';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import useDebounce from '../../../../../../../hooks/useDebounce';
import FormGroup from '../../../../../FormGroup';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerBackgroundControls = ({
  className,
  element = {},
  onElementConfigurationChange,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);

  const { printAssets, printAssetsTotal, setSearchQuery } =
    useAccountPrintAssets({
      printAssetType: PRINT_ASSET_TYPES.BACKGROUND,
    });

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  const classes = classNames({
    [className]: !!className,
  });

  const { cardElementConfiguration } = element;
  const [color, setColor] = useState(
    cardElementConfiguration?.background_color,
  );
  const { visible: noticeVisible, onDismiss: onNoticeDismiss } =
    useDismissiblePersistent('CARD_DESIGNER_BACKGROUND_NOTICE');

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      onElementConfigurationChange({
        element,
        elementConfig: {
          asset_image_id: assetImage.id,
        },
      });
    },
  });

  return (
    <div className={classes}>
      <div className="card-designer-inline-color-control mb-2">
        <Label id="background-control">Background Color</Label>
        <CardDesignerCardElementColorControl
          defaultColor={cardElementConfiguration?.background_color ?? undefined}
          onChange={hex => {
            setColor(hex);
            onElementConfigurationChange({
              element,
              elementConfig: {
                background_color: hex,
              },
            });
          }}
        />
        <Input id="background-control" value={color} readOnly />
      </div>

      <Label id="background-control">Background Image</Label>
      <ImageUpload
        id="card-designer-background-image"
        buttonText="Upload an image"
        loading={uploading}
        disabled={uploading}
        image={
          cardElementConfiguration?.asset_image
            ? {
                id: cardElementConfiguration.asset_image.id,
                thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
                  size_code: size,
                  url: cardElementConfiguration.asset_image.url,
                  width: 0,
                  height: 0,
                })),
              }
            : null
        }
        onInputChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        onImageDismiss={() => {
          onUploadReset();
          onElementConfigurationChange({
            element,
            elementConfig: {
              asset_image_id: null,
            },
          });
        }}
      />
      {noticeVisible && (
        <Alert
          type="info"
          onDismiss={onNoticeDismiss}
          className="mt-2 text-primary text-extra-bold text-xs"
        >
          Upload a custom background!
        </Alert>
      )}
      {printAssetsTotal > 0 && (
        <Fragment>
          <FormGroup
            id="query"
            name="query"
            label="Query"
            labelSrOnly
            className="mt-2"
            placeholder="Search your backdrops.."
            onChange={({ target }) => setQuery(target.value)}
            value={query}
          />

          <div className="card-designer-print-assets-container">
            {printAssets.map(printAsset => (
              <Button
                className="card-designer-print-assets-item"
                btnStyle="reset"
                onClick={() => {
                  onElementConfigurationChange({
                    element,
                    elementConfig: {
                      existing_asset_image_id: printAsset.asset_image.id,
                    },
                  });
                }}
              >
                <img
                  src={printAsset.asset_image.url}
                  alt={printAsset.name}
                  className="card-designer-print-assets-item-image"
                />
              </Button>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  );
};

CardDesignerBackgroundControls.propTypes = propTypes;

export default CardDesignerBackgroundControls;
